/**
 * formatChartDataForClipboard - Format chart data as Markdown for LLM chat
 *
 * Formats graha positions and panchanga data for clipboard copy.
 * Used by Badge C in UnifiedChartTab.
 */

import { devWarn, devError } from '../../../../utils/devLog';
import { JatakaApiResponse, ApiGrahaPosition } from '../../../../types/JatakaApiTypes';

/**
 * Convert decimal degrees to DMS format
 * @param decimal - Decimal degrees
 * @returns DMS string like "10° 55' 42.00""
 */
function decimalToDMS(decimal: number): string {
    const degrees = Math.floor(decimal);
    const minutesDecimal = (decimal - degrees) * 60;
    const minutes = Math.floor(minutesDecimal);
    const seconds = (minutesDecimal - minutes) * 60;

    return `${degrees}° ${minutes.toString().padStart(2, '0')}' ${seconds.toFixed(2).padStart(5, '0')}"`;
}

/**
 * Format a single graha row for the table
 * Full data: Name, Rashi, Degrees in Rashi (DMS), Longitude, Nakshatra, Pada, Navamsha, Speed
 */
function formatGrahaRow(graha: ApiGrahaPosition): string {
    const retrograde = graha.is_retrograde ? ' (R)' : '';
    const rasiIndex = Math.floor(graha.longitude / 30);
    const degreesInRashi = graha.longitude - (rasiIndex * 30);
    const degreesInRashiDMS = decimalToDMS(degreesInRashi);

    return `| ${graha.name}${retrograde} | ${graha.rasi_name} | ${degreesInRashiDMS} | ${graha.longitude.toFixed(6)}° | ${graha.nakshatra_name} | ${graha.nakshatra_pada} | ${graha.navamsa_rasi} | ${graha.speed.toFixed(6)} |`;
}

/**
 * Format graha positions as Markdown table
 */
function formatGrahaPositions(grahaPositions: JatakaApiResponse['graha_positions']): string {
    const lines: string[] = [
        '## Graha Positions',
        '',
        '| Graha | Rashi | Degrees in Rashi | Longitude | Nakshatra | Pada | Navamsha | Speed |',
        '|-------|-------|------------------|-----------|-----------|------|----------|-------|'
    ];

    // Find Lagna - either in separate field or in planets array
    const lagnaFromField = grahaPositions.lagna;
    const lagnaFromPlanets = grahaPositions.planets.find(p => p.name === 'Lagna');
    const lagna = lagnaFromField || lagnaFromPlanets;

    // Add Lagna first if present
    if (lagna) {
        lines.push(formatGrahaRow(lagna));
    }

    // Add all planets (excluding Lagna)
    for (const planet of grahaPositions.planets) {
        if (planet.name === 'Lagna') continue;
        lines.push(formatGrahaRow(planet));
    }

    return lines.join('\n');
}

/**
 * Extract value from panchanga string
 * Examples:
 * - "Chaturdashi (Sk) - Rikta (46.66% left)" -> "Chaturdashi (Shukla)"
 * - "Thursday (Gu)" -> "Thursday (Guru)"
 * - "Chandra (20/24)" -> "Chandra (20/24)"
 */
function cleanPanchangaValue(value: string): string {
    // Return as-is for maximum information
    return value;
}

/**
 * Format panchanga as Markdown table with full details
 */
function formatPanchanga(
    panchanga: JatakaApiResponse['panchanga'],
    astronomical: JatakaApiResponse['astronomical'],
    detailedPanchanga?: JatakaApiResponse['detailed_panchanga']
): string {
    const lines: string[] = [
        '## Panchanga',
        '',
        '| Parameter | Value |',
        '|-----------|-------|'
    ];

    // Samvat & Masa
    lines.push(`| Lunar Year/Month | ${panchanga.lunar_year_month} |`);

    if (detailedPanchanga?.masa_details) {
        const masa = detailedPanchanga.masa_details;
        lines.push(`| Masa | ${masa.name} (${masa.alternative}) - ${masa.season} |`);
        lines.push(`| Masa Deity | ${masa.ruling_deity} |`);
        lines.push(`| Masa Completion | ${masa.completion.toFixed(2)}% |`);
    }

    // Tithi
    lines.push(`| Tithi | ${panchanga.tithi} |`);
    if (detailedPanchanga?.tithi_details) {
        const tithi = detailedPanchanga.tithi_details;
        lines.push(`| Tithi Details | ${tithi.name} (${tithi.paksha} ${tithi.paksha_tithi}) - ${tithi.kind} |`);
        lines.push(`| Tithi Ruler | ${tithi.ruling_planet} |`);
        lines.push(`| Tithi Completion | ${tithi.completion.toFixed(2)}% |`);
    }

    // Nakshatra
    lines.push(`| Nakshatra | ${panchanga.nakshatra} |`);
    if (detailedPanchanga?.nakshatra_details) {
        const nak = detailedPanchanga.nakshatra_details;
        lines.push(`| Nakshatra Ruler | ${nak.ruling_planet} |`);
        lines.push(`| Nakshatra Completion | ${nak.completion.toFixed(2)}% |`);
        lines.push(`| Nakshatra Remaining | ${nak.remaining.toFixed(6)}° (${nak.remaining_arc_minutes.toFixed(2)}') |`);
    }

    // Yoga
    lines.push(`| Yoga | ${panchanga.yoga} |`);
    if (detailedPanchanga?.yoga_details) {
        const yoga = detailedPanchanga.yoga_details;
        lines.push(`| Yoga Ruler | ${yoga.ruling_planet} |`);
        lines.push(`| Yoga Completion | ${yoga.completion.toFixed(2)}% |`);
    }

    // Karana
    lines.push(`| Karana | ${panchanga.karana} |`);
    if (detailedPanchanga?.karana_details) {
        const karana = detailedPanchanga.karana_details;
        lines.push(`| Karana Type | ${karana.type} |`);
        lines.push(`| Karana Ruler | ${karana.ruling_planet} |`);
    }

    // Vara
    lines.push(`| Vara | ${panchanga.weekday} |`);
    if (detailedPanchanga?.vara_details) {
        const vara = detailedPanchanga.vara_details;
        lines.push(`| Vara Sanskrit | ${vara.sanskrit} |`);
        lines.push(`| Vara Ruler | ${vara.ruling_planet} |`);
    }

    // Hora
    lines.push(`| Hora | ${panchanga.hora_lord} |`);
    if (detailedPanchanga?.hora_details) {
        const hora = detailedPanchanga.hora_details;
        lines.push(`| Hora Planet | ${hora.current_planet} |`);
        lines.push(`| Hora Period | ${hora.period} (${hora.hour_number}/24) |`);
    }

    // Muhurta
    lines.push(`| Muhurta | ${panchanga.muhurta} |`);
    if (detailedPanchanga?.muhurta_details) {
        const muhurta = detailedPanchanga.muhurta_details;
        lines.push(`| Muhurta Name | ${muhurta.name} (${muhurta.number}/30) |`);
        lines.push(`| Muhurta Lord | ${muhurta.lord} |`);
        lines.push(`| Muhurta Ruler | ${muhurta.graha_ruler} |`);
        lines.push(`| Muhurta Completion | ${muhurta.completion.toFixed(2)}% |`);
    }

    // Ghati
    lines.push(`| Ghati | ${panchanga.ghati} |`);
    if (detailedPanchanga?.ghati_details) {
        const ghati = detailedPanchanga.ghati_details;
        lines.push(`| Ghati Name | ${ghati.name} (${ghati.number}/60) |`);
        lines.push(`| Ghati Lord | ${ghati.lord} |`);
        lines.push(`| Janma Ghati | ${ghati.janma_ghati.toFixed(6)} |`);
        lines.push(`| Ghati Traditional | ${ghati.traditional} |`);
    }

    // Astronomical
    lines.push(`| Sunrise | ${astronomical.sunrise} |`);
    lines.push(`| Sunset | ${astronomical.sunset} |`);
    const suryaLong = astronomical.sunrise_surya_longitude;
    const suryaLongFormatted = typeof suryaLong === 'number' ? suryaLong.toFixed(6) : suryaLong;
    lines.push(`| Sunrise Surya Longitude | ${suryaLongFormatted}° |`);
    lines.push(`| Ayanamsa | ${astronomical.ayanamsa} (${astronomical.ayanamsa_system}) |`);

    return lines.join('\n');
}

/**
 * Format basic info header
 * Note: Location excluded for privacy
 */
function formatBasicInfo(basicInfo: JatakaApiResponse['basic_info']): string {
    const lines: string[] = [
        '## Bhrigu Hora chart Info',
        '',
        `- **Date**: ${basicInfo.date}`,
        `- **Time**: ${basicInfo.time}`,
        `- **Timezone**: ${basicInfo.timezone}`
    ];

    return lines.join('\n');
}

/**
 * Main function - format complete chart data for clipboard
 *
 * @param apiResponse - Complete Jataka API response
 * @returns Formatted Markdown string
 */
export function formatChartDataForClipboard(apiResponse: JatakaApiResponse): string {
    const sections: string[] = [];

    // Basic info
    if (apiResponse.basic_info) {
        sections.push(formatBasicInfo(apiResponse.basic_info));
    }

    // Graha positions
    if (apiResponse.graha_positions) {
        sections.push(formatGrahaPositions(apiResponse.graha_positions));
    }

    // Panchanga
    if (apiResponse.panchanga && apiResponse.astronomical) {
        sections.push(formatPanchanga(
            apiResponse.panchanga,
            apiResponse.astronomical,
            apiResponse.detailed_panchanga
        ));
    }

    return sections.join('\n\n');
}

/**
 * Copy text to clipboard with fallback for mobile devices
 *
 * @param text - Text to copy
 * @returns Promise<boolean> - Success status
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    // Try modern Clipboard API first (requires HTTPS or localhost)
    try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (error) {
        devWarn('Clipboard API failed, trying fallback:', error);
    }

    // Fallback for mobile and older browsers
    try {
        const textArea = document.createElement('textarea');
        textArea.value = text;

        // Prevent zooming on iOS
        textArea.style.fontSize = '16px';

        // Make it invisible but still selectable
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        textArea.style.opacity = '0';

        // Prevent scroll jump
        textArea.style.zIndex = '-1';

        document.body.appendChild(textArea);

        // iOS specific: need to use setSelectionRange
        textArea.focus();
        textArea.setSelectionRange(0, text.length);

        const success = document.execCommand('copy');
        document.body.removeChild(textArea);

        return success;
    } catch (error) {
        devError('Failed to copy to clipboard:', error);
        return false;
    }
}
